import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import Button from 'components/App/Button';
import RefundMethodField from 'components/RefundMethodField';

// Pure so it's unit-testable. Default (no choices) is "needs a gateway ref" — true for the
// no-choice refund actions (mark-refunded etc., refundField swaps it for the method chooser) and
// for a no-choice/no-refundField action like the hotel manual-only fallback. `noGatewayRef` opts
// an action fully out of that default: refund/request has nothing to reference yet, it only
// collects an amount and a note (bug fix, onepay-refund.md §6 note).
export const needsGatewayRefFor = (choices, choice, noGatewayRef) => {
  if (noGatewayRef) return false;
  return !choices.length || !!choice?.needsGatewayRef;
};

// Collects a note and the gateway reference for mark-refunded / resolve-* actions.
// With `choices`, the admin picks a resolution first; only a choice marked needsGatewayRef
// asks for the gateway ref. With `amountField`, the admin also enters a capped VND amount
// (refund/request: never more than what the customer paid). With `refundField`, a gateway-ref
// spot instead becomes the OnePay/manual method choice (onepay-refund.md §3.5) — only the five
// refund actions pass it; resolve-schedule-change's REFUNDED still collects a bare gateway ref.
// `noGatewayRef` drops the gateway-ref spot entirely (refund/request: amount + note only).
function ModalFlightNote(props) {
  const {
    isOpen, title, description, choices, amountField, refundField, noGatewayRef,
    gatewayRefOptional, isLoading, onConfirm, onClose,
  } = props;

  const [resolution, setResolution] = useState('');
  const [gatewayRef, setGatewayRef] = useState('');
  const [method, setMethod] = useState('ONEPAY');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (isOpen) {
      setResolution('');
      setGatewayRef('');
      setMethod('ONEPAY');
      setAmount('');
      setNote('');
    }
  }, [isOpen]);

  const choice = choices.find((item) => item.value === resolution);
  const needsGatewayRef = needsGatewayRefFor(choices, choice, noGatewayRef);
  // With refundField, OnePay needs no gateway ref at all; manual still does.
  const needsGatewayRefInput = needsGatewayRef && (!refundField || method === 'MANUAL');
  const amountValue = Number(amount);
  const isAmountValid = !amountField
    || (Number.isInteger(amountValue) && amountValue >= 1 && amountValue <= amountField.max);
  const isComplete = (!choices.length || !!choice)
    && !!note.trim()
    && (!needsGatewayRefInput || gatewayRefOptional || !!gatewayRef.trim())
    && isAmountValid;

  const onSubmit = () => onConfirm({
    ...(choice && { resolution }),
    ...(amountField && { amount: amountValue }),
    ...(needsGatewayRef && refundField && { method }),
    note: note.trim(),
    ...(needsGatewayRefInput && { gatewayRef: gatewayRef.trim() }),
  });

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        {description && <p>{description}</p>}
        {choices.map((item) => (
          <div className="form-check mb-2" key={item.value}>
            <Label className="form-check-label">
              <Input
                type="radio"
                name="flight-resolution"
                className="form-check-input"
                checked={resolution === item.value}
                onChange={() => setResolution(item.value)}
              />
              {item.label}
            </Label>
          </div>
        ))}
        {needsGatewayRef && refundField && (
          <RefundMethodField
            method={method}
            onMethodChange={setMethod}
            amountLabel={refundField.amountLabel}
            gatewayRef={gatewayRef}
            onGatewayRefChange={setGatewayRef}
            hasPendingRefund={refundField.hasPendingRefund}
            forceManualOn={refundField.forceManualOn}
          />
        )}
        {needsGatewayRef && !refundField && (
          <>
            <Label htmlFor="flight-gateway-ref">
              {`Mã tham chiếu hoàn tiền trên cổng thanh toán${gatewayRefOptional ? '' : ' *'}`}
            </Label>
            <Input
              id="flight-gateway-ref"
              className="mb-3"
              value={gatewayRef}
              onChange={(e) => setGatewayRef(e.target.value)}
            />
          </>
        )}
        {amountField && (
          <>
            <Label htmlFor="flight-amount">
              {amountField.label}
              {' *'}
              {amountField.hint && <div className="font-size-12 text-muted">{amountField.hint}</div>}
            </Label>
            <Input
              id="flight-amount"
              className="mb-3"
              type="number"
              min={1}
              max={amountField.max}
              step={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </>
        )}
        <Label htmlFor="flight-note">Ghi chú *</Label>
        <Input
          id="flight-note"
          type="textarea"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </ModalBody>
      <ModalFooter>
        <Button outline onClick={onClose}>Huỷ</Button>
        <Button
          loading={isLoading}
          disabled={isLoading || !isComplete}
          onClick={onSubmit}
        >
          Xác nhận
        </Button>
      </ModalFooter>
    </Modal>
  );
}

ModalFlightNote.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  choices: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    needsGatewayRef: PropTypes.bool,
  })),
  amountField: PropTypes.shape({
    label: PropTypes.string.isRequired,
    max: PropTypes.number.isRequired,
    hint: PropTypes.string,
  }),
  refundField: PropTypes.shape({
    amountLabel: PropTypes.string.isRequired,
    hasPendingRefund: PropTypes.bool,
    forceManualOn: PropTypes.number,
  }),
  noGatewayRef: PropTypes.bool,
  // The bare gateway-ref field may stay empty (hotel manual refund: BE takes it as optional).
  gatewayRefOptional: PropTypes.bool,
  isLoading: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

ModalFlightNote.defaultProps = {
  description: '',
  choices: [],
  amountField: null,
  refundField: null,
  noGatewayRef: false,
  gatewayRefOptional: false,
  isLoading: false,
};

export default memo(ModalFlightNote);
